import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import adminService from "../api/adminService";
import { getRoles } from "../api/adminRoleService";
import CountryPage from "./CountryPage";
import CityPage from "./CityPage";
import PostalCodePage from "./PostalCodePage";
import AddressPage from "./AddressPage";
import Shipment from "../components/Shipment";
import ParcelPage from "./ParcelPage";
import GeoFencePage from "./GeoFencePage";
import SmartDeliveryPage from "./admin/SmartDeliveryPage";
import SmartContractPage from "./legalDepartment/SmartContractPage";
import WarehousePage from "./logistics/WarehousePage";
import DriverPage from "./logistics/DriverPage";
import DispatchCenterPage from "./logistics/DispatchCenterPage";
import DemandForecast from "../components/DemandForecast";
import SendSmsComponent from "../components/SendSmsComponent";
import DashboardStats from "../components/dashboard/DashboardStats";

const AdminPage = () => {
    const navigate = useNavigate();
    const [roles, setRoles] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [tab, setTab] = useState("dashboard");
    const [selectedRoles, setSelectedRoles] = useState({});
    useEffect(() => {
        const token =
            localStorage.getItem("authToken") ||
            sessionStorage.getItem("authToken") ||
            localStorage.getItem("token") ||
            sessionStorage.getItem("token");

        const role =
            localStorage.getItem("role") ||
            sessionStorage.getItem("role");

        if (!token) {
            navigate("/login");
            return;
        }

        if (role && !["admin", "administrator"].includes(role.toLowerCase())) {
            setError("Access denied.");
            setLoading(false);
            return;
        }

        loadUsers();
        loadRoles();
    }, [navigate]);
    const loadRoles = async () => {
        try {
            const data = await getRoles();
            setRoles(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error("Failed to load roles", e);
        }
    };
    const loadUsers = async () => {
        setLoading(true);
        setError("");

        try {
            const data = await adminService.getAllUsers();
            setUsers(Array.isArray(data) ? data : []);
        } catch (e) {
            const msg =
                e?.response?.data?.message ||
                e?.response?.data?.title ||
                e?.message ||
                "Failed to load users.";

            if (e?.response?.status === 401 || e?.response?.status === 403) {
                localStorage.removeItem("authToken");
                sessionStorage.removeItem("authToken");
                localStorage.removeItem("token");
                sessionStorage.removeItem("token");
                localStorage.removeItem("role");
                sessionStorage.removeItem("role");
                navigate("/login");
            } else {
                setError(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDisable2FA = async (userId) => {
        try {
            setError("");
            setSuccess("");
            await adminService.disableUserTwoFactor(userId);
            setSuccess("2FA disabled successfully.");
            await loadUsers();
        } catch (e) {
            setError(
                e?.response?.data?.message ||
                e?.message ||
                "Failed to disable 2FA."
            );
        }
    };
    const handleChangeRole = async (userId, roleId) => {
        try {
            setError("");
            setSuccess("");

            await adminService.changeUserRole(userId, roleId);

            setSuccess("Role changed successfully.");
            await loadUsers();
        } catch (e) {
            setError(
                e?.response?.data?.message ||
                e?.message ||
                "Failed to change role."
            );
        }
    };
    const handleDeleteAdmin = async (adminId) => {
        if (!window.confirm("Delete this admin?")) return;

        try {
            setError("");
            setSuccess("");
            await adminService.deleteAdmin(adminId);
            setSuccess("Admin deleted successfully.");
            await loadUsers();
        } catch (e) {
            setError(
                e?.response?.data?.message ||
                e?.message ||
                "Failed to delete admin."
            );
        }
    };

    const renderUsersTab = () => (
        <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold mb-4">Users</h2>

            {loading && <p>Loading...</p>}
            {error && <p className="text-red-500 mb-3">{error}</p>}
            {success && <p className="text-green-600 mb-3">{success}</p>}

            {!loading && users.length === 0 ? (
                <p className="text-gray-600">No users found.</p>
            ) : (
                <table className="min-w-full border">
                    <thead>
                        <tr>
                            <th className="border px-4 py-2">ID</th>
                            <th className="border px-4 py-2">Username</th>
                            <th className="border px-4 py-2">Email</th>
                            <th className="border px-4 py-2">Role</th>
                            <th className="border px-4 py-2">2FA</th>
                            <th className="border px-4 py-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((u) => {
                            const userId = u.id ?? u.userId;
                            const roleName =
                                u.role?.name ??
                                u.roleName ??
                                u.role ??
                                "User";

                            const username =
                                u.userName ??
                                u.username ??
                                u.name ??
                                u.fullName ??
                                "—";

                            const isAdmin = String(roleName).toLowerCase() === "admin";
                            const twoFaEnabled =
                                u.isTwoFactorEnabled === true ||
                                u.twoFactorEnabled === true;

                            return (
                                <tr key={userId}>
                                    <td className="border px-4 py-2">{userId}</td>
                                    <td className="border px-4 py-2">{username}</td>
                                    <td className="border px-4 py-2">{u.email ?? "—"}</td>
                                    <td className="border px-4 py-2">{roleName}</td>
                                    <td className="border px-4 py-2">
                                        {twoFaEnabled ? "Enabled" : "Disabled"}
                                    </td>
                                    <td className="border px-4 py-2">
                                        <div className="flex gap-2 flex-wrap items-center">
                                            <select
                                                value={selectedRoles[userId] ?? u.roleId ?? ""}
                                                onChange={(e) =>
                                                    setSelectedRoles((prev) => ({
                                                        ...prev,
                                                        [userId]: Number(e.target.value),
                                                    }))
                                                }
                                                className="border rounded px-2 py-1 text-black"
                                            >
                                                <option value="">Assign Role</option>

                                                {roles.map((role) => (
                                                    <option key={role.id} value={role.id}>
                                                        {role.name}
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleChangeRole(userId, selectedRoles[userId] ?? u.roleId)
                                                }
                                                className="bg-blue-600 text-white px-2 py-1 rounded"
                                            >
                                                Apply Role
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => handleDisable2FA(userId)}
                                                className="bg-yellow-500 text-white px-2 py-1 rounded"
                                            >
                                                Disable 2FA
                                            </button>
                                            <button onClick={() => navigate("/admin/executive-dashboard")}>
                                                Executive Dashboard
                                            </button>
                                            {isAdmin && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteAdmin(userId)}
                                                    className="bg-red-600 text-white px-2 py-1 rounded"
                                                >
                                                    Delete Admin
                                                </button>

                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
    const renderContent = () => {
        switch (tab) {
            case "dashboard":
                return <DashboardStats />;
            case "users":
                return renderUsersTab();
            case "countries":
                return <CountryPage />;
            case "cities":
                return <CityPage />;
            case "postalcodes":
                return <PostalCodePage />;
            case "addresses":
                return <AddressPage />;
            case "shipments":
                return <Shipment />;
            case "parcels":
                return <ParcelPage />;
            case "geofence":
                return <GeoFencePage />;
            case "smartdelivery":
                return <SmartDeliveryPage />;
            case "smartcontracts":
                return <SmartContractPage />;
            case "warehouses":
                return <WarehousePage />;
            case "drivers":
                return <DriverPage />;
            case "dispatchcenters":
                return <DispatchCenterPage />;
            case "demandforecast":
                return <DemandForecast />;
            case "sms":
                return <SendSmsComponent />;
            default:
                return renderUsersTab();
        }
    };

    return (
        <div className="w-full">
            {renderContent()}
        </div>
    );
};

export default AdminPage; 